// Shared Documents List Component
// BP-10-04: Attorney Frontend

import { useState } from 'react';
import {
  FileText,
  Download,
  Eye,
  Calendar,
  User,
  Search,
  Filter,
  FolderOpen,
  File,
  Image as ImageIcon,
  FileSpreadsheet,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';

interface SharedDocument {
  id: string;
  title: string;
  originalFilename: string;
  fileType: string;
  mimeType: string;
  fileSizeBytes: number;
  documentType: string;
  documentDate?: string;
  providerName?: string;
  sharedAt: string;
  sharedBy?: {
    firstName: string;
    lastName: string;
  };
}

interface SharedDocumentsListProps {
  documents: SharedDocument[];
  onDownload: (documentId: string) => void;
  onPreview?: (documentId: string) => void;
  isLoading?: boolean;
}

const fileTypeIcons: Record<string, React.ComponentType<any>> = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  png: ImageIcon,
  jpg: ImageIcon,
  jpeg: ImageIcon,
  gif: ImageIcon,
  default: File,
};

const documentTypeLabels: Record<string, string> = {
  medical_record: 'Medical Record',
  iep: 'IEP Document',
  legal: 'Legal Document',
  insurance: 'Insurance',
  financial: 'Financial',
  other: 'Other',
};

export function SharedDocumentsList({
  documents,
  onDownload,
  onPreview,
  isLoading = false,
}: SharedDocumentsListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getFileIcon = (fileType: string) => {
    const ext = fileType.toLowerCase().replace('.', '');
    return fileTypeIcons[ext] || fileTypeIcons.default;
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.originalFilename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || doc.documentType === filterType;
    return matchesSearch && matchesFilter;
  });

  const documentTypes = Array.from(new Set(documents.map((d) => d.documentType)));

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
          />
        </div>
        
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage appearance-none bg-white"
          >
            <option value="all">All Types</option>
            {documentTypes.map((type) => (
              <option key={type} value={type}>
                {documentTypeLabels[type] || type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">
            {searchQuery || filterType !== 'all'
              ? 'No documents match your search'
              : 'No documents have been shared yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredDocuments.map((doc) => {
            const FileIcon = getFileIcon(doc.fileType);
            
            return (
              <div
                key={doc.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:border-sage/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* File Icon */}
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileIcon className="w-5 h-5 text-gray-500" />
                  </div>

                  {/* Document Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-navy truncate">
                      {doc.title || doc.originalFilename}
                    </h4>
                    
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                        {documentTypeLabels[doc.documentType] || doc.documentType}
                      </span>
                      <span>{formatFileSize(doc.fileSizeBytes)}</span>
                      {doc.documentDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(doc.documentDate)}
                        </span>
                      )}
                      {doc.providerName && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {doc.providerName}
                        </span>
                      )}
                    </div>

                    {/* Shared Info */}
                    <p className="text-xs text-gray-400 mt-2">
                      Shared {formatDate(doc.sharedAt)}
                      {doc.sharedBy && (
                        <> by {doc.sharedBy.firstName} {doc.sharedBy.lastName}</>
                      )}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {onPreview && (
                      <button
                        onClick={() => onPreview(doc.id)}
                        className="p-2 text-gray-400 hover:text-sage hover:bg-sage/10 rounded-lg transition-colors"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onDownload(doc.id)}
                      className="p-2 text-gray-400 hover:text-sage hover:bg-sage/10 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Document Count */}
      {filteredDocuments.length > 0 && (
        <p className="text-sm text-gray-500 text-center">
          Showing {filteredDocuments.length} of {documents.length} documents
        </p>
      )}
    </div>
  );
}

export default SharedDocumentsList;
